import React, { useState } from 'react';
import { AudioButton } from './AudioButton';

export const GrammatikSection = ({ data, isActive, items, accentColor = '#a78bfa' }) => {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState({});

  // Support both single data object and items array
  const grammarItems = items || (data ? [data] : []);
  
  // For single data mode, check isActive; for items mode, always show
  if (data && !isActive) return null;
  if (!grammarItems || grammarItems.length === 0) return null;

  const handleAnswer = (itemIdx, exIdx, optIdx) => {
    if (showResults[itemIdx]) return;
    setAnswers(prev => ({ ...prev, [`${itemIdx}-${exIdx}`]: optIdx }));
  };

  const getScore = (itemIdx, exercises) => {
    let correct = 0;
    exercises.forEach((ex, i) => { if (answers[`${itemIdx}-${i}`] === ex.ans) correct++; });
    return { correct, total: exercises.length, pct: Math.round((correct / exercises.length) * 100) };
  };

  const reset = (itemIdx) => { 
    const newAnswers = { ...answers };
    Object.keys(newAnswers).forEach(key => {
      if (key.startsWith(`${itemIdx}-`)) delete newAnswers[key];
    });
    setAnswers(newAnswers);
    setShowResults(prev => ({ ...prev, [itemIdx]: false }));
  };

  const renderGrammarItem = (gramData, itemIdx) => {
    if (!gramData) return null;
    
    return (
      <div key={itemIdx} className="grammatik-item" style={{ marginBottom: itemIdx < grammarItems.length - 1 ? '32px' : 0 }}>
        <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px', color: accentColor }}>
          {gramData.title.de}
        </h2>
        <p style={{ color: '#94a3b8', marginBottom: '20px', fontSize: '14px' }}>
          {gramData.title.en} · <span style={{ color: accentColor, direction: 'rtl' }}>{gramData.title.ar}</span>
        </p>

        {/* Rules */}
        <div style={{ background: `linear-gradient(135deg, ${accentColor}1a, ${accentColor}0d)`, border: `1px solid ${accentColor}4d`, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>📐</span>
            <strong style={{ color: accentColor, fontSize: '15px' }}>Grammatik-Regeln · Grammar Rules · <span style={{ direction: 'rtl' }}>قواعد نحوية</span></strong>
          </div>
          {gramData.rules.map((rule, ri) => (
            <div key={`rule-${itemIdx}-${rule.de?.slice(0, 20) || ri}`} style={{ marginBottom: ri < gramData.rules.length - 1 ? '12px' : 0, paddingBottom: ri < gramData.rules.length - 1 ? '12px' : 0, borderBottom: ri < gramData.rules.length - 1 ? `1px solid ${accentColor}26` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <p style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '500', marginBottom: '4px', flex: 1 }}>{rule.de}</p>
                <AudioButton text={rule.de} size="sm" voice="nova" />
              </div>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '2px' }}>{rule.en}</p>
              <p style={{ color: accentColor, fontSize: '13px', direction: 'rtl' }}>{rule.ar}</p>
            </div>
          ))}
        </div>

        {/* Exercises */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ fontSize: '20px' }}>✏️</span>
          <strong style={{ color: '#e2e8f0', fontSize: '15px' }}>Übungen · Exercises · <span style={{ direction: 'rtl', color: accentColor }}>تمارين</span></strong>
        </div>

        {gramData.exercises.map((ex, i) => (
          <div key={`ex-${itemIdx}-${ex.q?.slice(0, 20) || i}`} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px', marginBottom: '12px' }} data-testid={`grammatik-ex-${itemIdx}-${i}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#e2e8f0' }}>
                <strong style={{ color: accentColor }}>Übung {i + 1}:</strong> {ex.q}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ex.opts.map((opt, oi) => {
                let bg = '#0f172a';
                let border = '#334155';
                if (answers[`${itemIdx}-${i}`] === oi) { bg = `${accentColor}1a`; border = accentColor; }
                if (showResults[itemIdx]) {
                  if (oi === ex.ans) { bg = 'rgba(34,197,94,0.1)'; border = '#22c55e'; }
                  else if (answers[`${itemIdx}-${i}`] === oi && oi !== ex.ans) { bg = 'rgba(239,68,68,0.1)'; border = '#ef4444'; }
                }
                return (
                  <div
                    key={`opt-${itemIdx}-${i}-${opt?.slice(0, 15) || oi}`}
                    onClick={() => handleAnswer(itemIdx, i, oi)}
                    style={{ background: bg, border: `2px solid ${border}`, borderRadius: '8px', padding: '10px 14px', cursor: showResults[itemIdx] ? 'default' : 'pointer', transition: 'all 0.2s', fontSize: '13px', color: '#e2e8f0' }}
                    data-testid={`grammatik-ex-${itemIdx}-${i}-opt-${oi}`}
                  >
                    {String.fromCharCode(65 + oi)}) {opt}
                  </div>
                );
              })}
            </div>
            {showResults[itemIdx] && answers[`${itemIdx}-${i}`] !== ex.ans && ex.hint && (
              <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', fontSize: '12px', color: '#fbbf24' }}>
                Hinweis: {ex.hint.de} · {ex.hint.en}
              </div>
            )}
          </div>
        ))}

        {/* Submit / Results */}
        {!showResults[itemIdx] ? (
          <button
            onClick={() => setShowResults(prev => ({ ...prev, [itemIdx]: true }))}
            style={{ width: '100%', padding: '14px', fontSize: '16px', marginTop: '12px', background: accentColor, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
            data-testid={`grammatik-submit-btn-${itemIdx}`}
          >
            Ergebnis anzeigen · Show Result · <span style={{ direction: 'rtl' }}>عرض النتيجة</span>
          </button>
        ) : (
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', border: `2px solid ${accentColor}`, borderRadius: '16px', padding: '24px', textAlign: 'center', marginTop: '16px' }} data-testid={`grammatik-results-${itemIdx}`}>
            <div style={{ fontSize: '48px', fontWeight: '700', color: accentColor }}>{getScore(itemIdx, gramData.exercises).pct}%</div>
            <div style={{ fontSize: '18px', color: '#94a3b8', margin: '8px 0 16px' }}>
              {getScore(itemIdx, gramData.exercises).correct} / {getScore(itemIdx, gramData.exercises).total} richtig · correct · <span style={{ direction: 'rtl' }}>صحيح</span>
            </div>
            <div style={{ fontSize: '14px', color: '#e2e8f0', marginBottom: '16px' }}>
              {getScore(itemIdx, gramData.exercises).pct >= 80 ? 'Ausgezeichnet! Excellent! ممتاز!' : getScore(itemIdx, gramData.exercises).pct >= 50 ? 'Gut gemacht! Good job! أحسنت!' : 'Weiter üben! Keep practicing! واصل التدريب!'}
            </div>
            <button
              onClick={() => reset(itemIdx)}
              style={{ padding: '10px 24px', background: accentColor, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
              data-testid={`grammatik-reset-btn-${itemIdx}`}
            >
              Nochmal · Again · <span style={{ direction: 'rtl' }}>مرة أخرى</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="grammatik-section" data-testid="grammatik-section">
      {grammarItems.map((item, idx) => renderGrammarItem(item, idx))}
    </div>
  );
};
